import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { useHotkeys } from 'react-hotkeys-hook';
import { MainBoundary } from '@frontend/error-boundary';
import { useTranslation } from '@frontend/i18n';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { Tray, useAlert } from '@frontend/design-system';
import { PanelContext, useCallInsightPanelShallowStore } from '../hooks/use-call-insight-panel-store';
import { useScreens } from '../hooks/use-screens';
import { CallInsightPanelSource, ScreenBaseProps, ScreenComponentMap, ScreenType } from '../types';
import { CallInsightPanelLoader } from './call-insight-panel-loader';
import { CallInsightScreen } from './screens/call-insight-screen';
import { CallPathMappingScreen } from './screens/call-path-mapping-screen';
import { CallQualityDrilldownScreen } from './screens/call-quality-drilldown-screen';
import { CallQualityScreen } from './screens/call-quality-screen';

const Screens: ScreenComponentMap = {
  [ScreenType.CALL_INSIGHTS]: CallInsightScreen,
  [ScreenType.CALL_QUALITY]: CallQualityScreen,
  [ScreenType.CALL_QUALITY_DRILLDOWN]: CallQualityDrilldownScreen,
  [ScreenType.CALL_PATH_MAPPING]: CallPathMappingScreen,
};

export const CallInsightDemoTray = () => {
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');

  const ctx = activeSource && contexts[activeSource];
  const currentScreenView = ctx?.callListOptions.currentScreenView;
  const { screenType, data: payload, setScreen, reset } = useScreens(currentScreenView ?? ScreenType.CALL_INSIGHTS);

  const Screen =
    (Screens[screenType] as React.ComponentType<ScreenBaseProps<typeof screenType>>) ||
    Screens[ScreenType.CALL_INSIGHTS];

  const handleClose = () => {
    reset();
    setShow(false, 'callInsight');
  };

  useEffect(() => {
    reset();
  }, [ctx?.selectedCall?.id, reset]);

  if (!activeSource || !ctx?.selectedCall) {
    return null;
  }

  return (
    <Tray mountTarget='[data-settings-modal-content]' width='medium' css={{ padding: 0 }} show onClose={handleClose}>
      <Screen
        source={activeSource}
        key={ctx.selectedCall.id}
        setScreen={setScreen}
        data={payload}
        onClose={handleClose}
      />
    </Tray>
  );
};

/**
 * Undocumented (and experimental) functionality to copy call shortlink to clipboard
 */
const useShortlinkCopy = (contexts: Record<CallInsightPanelSource, PanelContext>) => {
  const alerts = useAlert();
  const { t } = useTranslation();
  useHotkeys('ctrl+., cmd+.', () => {
    try {
      const { selectedCall } = contexts.callHistory;
      const copyString = `${window.location.origin + window.location.pathname}?linktype=callHistory&channelId=${
        selectedCall?.channelId
      }&locationId=${selectedCall?.locationId}`;
      navigator.clipboard.writeText(copyString);
      alerts.success(t('Shortlink copied successfully!'));
    } catch {
      alerts.warning(t('Failed to copy shortlink.'));
    }
  });
};

export const CallInsightPanel = () => {
  const { isOpen, close: closeGlobal } = useGlobalPanel('call-insight');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');
  const ref = useRef<HTMLDivElement | null>(null);

  useShortlinkCopy(contexts);

  const ctx = activeSource && contexts[activeSource];
  const currentScreenView = ctx?.callListOptions.currentScreenView;
  const { screenType, data: payload, setScreen, reset } = useScreens(currentScreenView ?? ScreenType.CALL_INSIGHTS);

  const Screen =
    (Screens[screenType] as React.ComponentType<ScreenBaseProps<typeof screenType>>) ||
    Screens[ScreenType.CALL_INSIGHTS];

  const handleClose = () => {
    reset();
    closeGlobal();
  };

  useEffect(() => {
    reset();
  }, [ctx?.selectedCall?.id, reset]);

  if (!activeSource || !ctx?.selectedCall) {
    return <CallInsightPanelLoader withHeader onClose={closeGlobal} />;
  }

  if (!isOpen) return null;

  return (
    <div ref={ref} css={styles.wrapper}>
      <SentryErrorBoundary fallback={MainBoundary}>
        <Screen
          source={activeSource}
          key={ctx.selectedCall.id}
          setScreen={setScreen}
          data={payload}
          onClose={handleClose}
          panelStyles={{ overflowY: 'auto' }}
        />
      </SentryErrorBoundary>
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
  `,
};
